/**
 * Preview Manager
 * 
 * Manages scene preview mode - switching between isolated product view
 * and contextual preview scenes (e.g., product on a desk).
 */

import * as THREE from 'three';
import { ModelLoader, type LoadedModel } from '../loaders';
import type { IManager, PreviewScene, CameraDefaults } from '../types';

export interface PreviewState {
  activeSceneId: string | null;
  isLoading: boolean;
}

export class PreviewManager implements IManager {
  private scene: THREE.Scene | null = null;
  private productModel: THREE.Object3D | null = null;
  private modelLoader: ModelLoader;
  
  private previewScenes: PreviewScene[] = [];
  private loadedScenes = new Map<string, LoadedModel>();
  private activeSceneId: string | null = null;
  private activeSceneModel: THREE.Object3D | null = null;
  
  private originalProductPosition = new THREE.Vector3();
  private originalProductRotation = new THREE.Euler();
  private originalProductScale = new THREE.Vector3(1, 1, 1);
  
  private onChangeCallback?: (sceneId: string | null) => void;
  private onCameraChangeCallback?: (defaults: CameraDefaults) => void;
  
  constructor(modelLoader?: ModelLoader) {
    this.modelLoader = modelLoader ?? new ModelLoader();
  }
  
  /**
   * Initialize with scene and preview configuration
   */
  public async init(
    scene: THREE.Scene,
    config: { 
      previewScenes: PreviewScene[];
      productModel: THREE.Object3D;
    }
  ): Promise<void> {
    this.scene = scene;
    this.productModel = config.productModel;
    this.previewScenes = config.previewScenes;
    
    // Store original product transform
    this.originalProductPosition.copy(this.productModel.position);
    this.originalProductRotation.copy(this.productModel.rotation);
    this.originalProductScale.copy(this.productModel.scale);
  }
  
  /**
   * Switch to a preview scene (or null for isolated view)
   */
  public async setActiveScene(sceneId: string | null): Promise<void> {
    console.log('[PreviewManager] setActiveScene:', sceneId);
    
    if (!this.scene || !this.productModel) {
      console.warn('[PreviewManager] No scene or product model');
      return;
    }
    
    // If switching to same scene, do nothing
    if (sceneId === this.activeSceneId) return;
    
    // Remove current preview scene if any
    if (this.activeSceneModel) {
      this.scene.remove(this.activeSceneModel);
      this.activeSceneModel = null;
    }
    
    // If switching to isolated view
    if (sceneId === null) {
      this.restoreProductTransform();
      this.activeSceneId = null;
      this.onChangeCallback?.(null);
      return;
    }
    
    // Find the preview scene config
    const previewScene = this.previewScenes.find((s) => s.id === sceneId);
    if (!previewScene) {
      console.warn(`Preview scene not found: ${sceneId}`);
      return;
    }
    
    // Load scene model if not cached
    let sceneModel = this.loadedScenes.get(sceneId);
    if (!sceneModel) {
      try {
        sceneModel = await this.modelLoader.load(previewScene.modelUrl);
        this.loadedScenes.set(sceneId, sceneModel);
      } catch (error) {
        console.error(`Failed to load preview scene: ${sceneId}`, error);
        return;
      }
    }
    
    // Add scene model to scene
    this.activeSceneModel = sceneModel.scene.clone();
    this.scene.add(this.activeSceneModel);
    
    // Position product within preview scene
    this.productModel.position.set(...previewScene.productPosition);
    
    if (previewScene.productRotation) {
      this.productModel.rotation.set(...previewScene.productRotation);
    }
    
    if (previewScene.productScale !== undefined) {
      this.productModel.scale.setScalar(previewScene.productScale);
    }
    
    // Update state
    this.activeSceneId = sceneId;
    this.onChangeCallback?.(sceneId);
    
    // Notify camera change if scene has camera override
    if (previewScene.cameraOverride) {
      this.onCameraChangeCallback?.(previewScene.cameraOverride);
    }
  }
  
  /**
   * Restore product to original transform
   */
  private restoreProductTransform(): void {
    if (!this.productModel) return;
    
    this.productModel.position.copy(this.originalProductPosition);
    this.productModel.rotation.copy(this.originalProductRotation);
    this.productModel.scale.copy(this.originalProductScale);
  }
  
  /**
   * Get all available preview scenes
   */
  public getScenes(): PreviewScene[] {
    return this.previewScenes;
  }
  
  /**
   * Get current state
   */
  public getState(): PreviewState {
    return {
      activeSceneId: this.activeSceneId,
      isLoading: false,
    };
  }
  
  /**
   * Get active scene ID
   */
  public getActiveSceneId(): string | null {
    return this.activeSceneId;
  }
  
  /**
   * Set change callback
   */
  public onChange(callback: (sceneId: string | null) => void): void {
    this.onChangeCallback = callback;
  }
  
  /**
   * Set camera change callback (called when scene has camera override)
   */
  public onCameraChange(callback: (defaults: CameraDefaults) => void): void {
    this.onCameraChangeCallback = callback;
  }
  
  /**
   * Toggle between isolated and first preview scene
   */
  public togglePreview(): string | null {
    if (this.activeSceneId === null && this.previewScenes.length > 0) {
      this.setActiveScene(this.previewScenes[0].id);
      return this.previewScenes[0].id;
    } else {
      this.setActiveScene(null);
      return null;
    }
  }
  
  /**
   * Preload all preview scenes
   */
  public async preloadAll(onProgress?: (loaded: number, total: number) => void): Promise<void> {
    const total = this.previewScenes.length;
    let loaded = 0;
    
    for (const scene of this.previewScenes) {
      if (!this.loadedScenes.has(scene.id)) {
        try {
          const model = await this.modelLoader.load(scene.modelUrl);
          this.loadedScenes.set(scene.id, model);
        } catch (error) {
          console.warn(`Failed to preload preview scene: ${scene.id}`, error);
        }
      }
      loaded++;
      onProgress?.(loaded, total);
    }
  }
  
  /**
   * Update (called each frame) - no-op for previews
   */
  public update(_deltaTime: number): void {
    // Preview scenes don't need per-frame updates
  }
  
  /**
   * Clean up resources
   */
  public dispose(): void {
    // Remove active scene
    if (this.activeSceneModel && this.scene) {
      this.scene.remove(this.activeSceneModel);
    }
    
    // Dispose loaded scene models
    for (const model of this.loadedScenes.values()) {
      model.scene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          object.geometry?.dispose();
          if (Array.isArray(object.material)) {
            object.material.forEach((m) => m.dispose());
          } else {
            object.material?.dispose();
          }
        }
      });
    }
    
    // Restore product transform
    this.restoreProductTransform();
    
    this.loadedScenes.clear();
    this.modelLoader.dispose();
    this.scene = null;
    this.productModel = null;
    this.activeSceneModel = null;
    this.activeSceneId = null;
  }
}
